import { useEffect, useState } from "react";
import { useCustom, useCustomMutation, usePermissions, useTranslate } from "@refinedev/core";
import { ActionBar, Button, FormCard, SectionHeader } from "@fawkes/ui";

import { zetBenaming, type Benaming } from "../../benaming";
import "../pensionplanning/AccommodatieBeheer.css";

// settings-pension: "Benaming" — noemt de organisatie haar pension een pension of een hotel. Eén keuze voor de
// hele organisatie (niet per vestiging); de backoffice en het klantportaal zetten het woord om in wat ze tonen.
// Wijzigen vraagt ROLE_PENSION_ADMIN, net als de api.

// De twee keuzes staan letterlijk en buiten de vertaling (data-benaming="vast"): anders zou "Pension" in een
// hotel zelf ook "Hotel" heten.
const KEUZES: { waarde: Benaming; naam: string; voorbeeld: string }[] = [
  { waarde: "PENSION", naam: "Pension", voorbeeld: "Pensionoverzichten, dieren in pension, pensiontarieven" },
  { waarde: "HOTEL", naam: "Hotel", voorbeeld: "Hoteloverzichten, dieren in hotel, hoteltarieven" },
];

export function PensionBenaming({ onBack }: { onBack: () => void }) {
  const t = useTranslate();
  const url = "pension/benaming";
  const { data, isLoading, isError } = useCustom<{ benaming: Benaming }>({ url, method: "get" });
  const { mutate, isLoading: bezig } = useCustomMutation();
  const { data: rechten } = usePermissions<string[]>();
  const magBeheren = (rechten ?? []).includes("ROLE_PENSION_ADMIN");

  const [keuze, setKeuze] = useState<Benaming>("PENSION");
  const [opgeslagen, setOpgeslagen] = useState(false);
  const [fout, setFout] = useState(false);

  const huidig = data?.data?.benaming;
  useEffect(() => {
    if (huidig) setKeuze(huidig);
  }, [huidig]);

  const bewaar = () => {
    setFout(false);
    setOpgeslagen(false);
    mutate(
      { url, method: "put", values: { benaming: keuze } },
      {
        onSuccess: () => {
          setOpgeslagen(true);
          // Meteen zichtbaar: terug naar Pension vraagt een herlaad, want wat al omgezet is staat er nog.
          if (keuze === "PENSION" && huidig === "HOTEL") globalThis.location?.reload();
          else zetBenaming(keuze);
        },
        onError: () => setFout(true),
      },
    );
  };

  return (
    <FormCard>
      <SectionHeader>{t("pensionBenaming.titel")}</SectionHeader>
      <div className="acc-beheer">
        {isLoading ? (
          <p className="fawkes-muted">{t("common.loading")}</p>
        ) : isError ? (
          <p className="fawkes-error">{t("pensionBenaming.laden")}</p>
        ) : (
          <form
            className="acc-kaart"
            aria-label={t("pensionBenaming.titel")}
            onSubmit={(e) => {
              e.preventDefault();
              bewaar();
            }}
          >
            <p className="acc-leeg" data-benaming="vast">
              Noemt u uw pension een hotel, kies dan Hotel. Overal in de backoffice en het klantportaal staat dan
              Hotel in plaats van Pension. Het geldt voor de hele organisatie; gegevens veranderen niet.
            </p>
            <fieldset className="acc-keuzes" disabled={!magBeheren} data-benaming="vast">
              <legend className="acc-verborgen">{t("pensionBenaming.titel")}</legend>
              {KEUZES.map((k) => (
                <label key={k.waarde} className="acc-keuze" data-gekozen={keuze === k.waarde ? "" : undefined}>
                  <input
                    type="radio"
                    name="benaming"
                    checked={keuze === k.waarde}
                    onChange={() => {
                      setKeuze(k.waarde);
                      setOpgeslagen(false);
                    }}
                  />
                  <span>
                    <strong>{k.naam}</strong>
                    <span>{k.voorbeeld}</span>
                  </span>
                </label>
              ))}
            </fieldset>
            {!magBeheren && <p className="acc-leeg">{t("pensionBenaming.alleenLezen")}</p>}
            {fout && (
              <p className="acc-fout" role="alert">
                {t("pensionBenaming.fout")}
              </p>
            )}
            {opgeslagen && <p className="acc-opgeslagen">{t("pensionBenaming.opgeslagen")}</p>}
            {magBeheren && (
              <ActionBar>
                <Button type="submit" disabled={bezig || keuze === huidig}>
                  {t("common.save")}
                </Button>
              </ActionBar>
            )}
          </form>
        )}
      </div>
      <div className="fawkes-list-toolbar">
        <Button variant="secondary" onClick={onBack}>
          {t("common.back")}
        </Button>
      </div>
    </FormCard>
  );
}
